const EOCD = 0x06054b50;
const CENTRAL = 0x02014b50;
const MAX_ENTRIES = 3000;
const MAX_FILE_BYTES = 2_000_000;
const MAX_TOTAL_BYTES = 400_000_000;

export interface ZipPhoto {
    name: string;
    bytes: Uint8Array;
}

function u16(data: Uint8Array, offset: number): number {
    return data[offset] | (data[offset + 1] << 8);
}

function u32(data: Uint8Array, offset: number): number {
    return (data[offset]
        | (data[offset + 1] << 8)
        | (data[offset + 2] << 16)
        | (data[offset + 3] << 24)) >>> 0;
}

function findEocd(data: Uint8Array): number {
    const min = Math.max(0, data.length - 22 - 65535);
    for (let i = data.length - 22; i >= min; i -= 1) {
        if (u32(data, i) === EOCD) return i;
    }
    return -1;
}

function isPhotoName(name: string): boolean {
    if (!name || name.endsWith('/') || name.includes('..') || name.includes('__MACOSX')) return false;
    return /\.(jpe?g|png|webp)$/i.test(name);
}

/**
 * Lee jpg, png y webp de un ZIP. El servidor vuelve a comprobar el nombre y los bytes.
 */
export async function unzipPhotos(
    data: Uint8Array,
    inflateRaw: (compressed: Uint8Array) => Promise<Uint8Array>,
): Promise<ZipPhoto[]> {
    const eocd = findEocd(data);
    if (eocd < 0) throw new Error('El archivo no es un ZIP');
    const count = u16(data, eocd + 10);
    const offset = u32(data, eocd + 16);
    if (count > MAX_ENTRIES || offset === 0xffffffff) {
        throw new Error('El ZIP es demasiado grande. Divídelo.');
    }

    const photos: ZipPhoto[] = [];
    let cursor = offset;
    let total = 0;
    for (let i = 0; i < count; i += 1) {
        if (cursor + 46 > data.length || u32(data, cursor) !== CENTRAL) {
            throw new Error('El ZIP está incompleto');
        }
        const flags = u16(data, cursor + 8);
        const method = u16(data, cursor + 10);
        const compressedSize = u32(data, cursor + 20);
        const uncompressedSize = u32(data, cursor + 24);
        const nameLength = u16(data, cursor + 28);
        const extraLength = u16(data, cursor + 30);
        const commentLength = u16(data, cursor + 32);
        const localOffset = u32(data, cursor + 42);
        const name = new TextDecoder().decode(data.subarray(cursor + 46, cursor + 46 + nameLength));
        cursor += 46 + nameLength + extraLength + commentLength;
        if ((flags & 1) !== 0 || !isPhotoName(name)) continue;
        if (method !== 0 && method !== 8) continue;
        if (uncompressedSize > MAX_FILE_BYTES) {
            throw new Error(`${name} supera 2 MB. Divídelo o comprímelo.`);
        }
        if (localOffset + 30 > data.length) throw new Error('El ZIP está incompleto');
        const localName = u16(data, localOffset + 26);
        const localExtra = u16(data, localOffset + 28);
        const start = localOffset + 30 + localName + localExtra;
        if (start + compressedSize > data.length) throw new Error('El ZIP está incompleto');
        const compressed = data.subarray(start, start + compressedSize);
        const bytes = method === 0 ? compressed : await inflateRaw(compressed);
        total += bytes.byteLength;
        if (total > MAX_TOTAL_BYTES) throw new Error('El ZIP supera 400 MB. Divídelo.');
        photos.push({ name, bytes });
    }
    return photos;
}
